import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { APP_NAME } from "../lib/constants";

export default function Home() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [firstWord, ...restWords] = APP_NAME.split(" ");
  const restOfName = restWords.join(" ");

  useEffect(() => {
    if (status === "authenticated") {
      navigate("/dashboard");
    }
  }, [status, navigate]);

  return (
    <div className="h-screen w-full bg-white text-slate-900 overflow-hidden selection:bg-primary/30 relative flex flex-col justify-between">
      
      {/* Background with Glassmorphism */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden bg-slate-50">
        {/* Bright & Vibrant Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-blue-400/30 blur-[120px] animate-pulse" style={{ animationDuration: '7s' }} />
        <div className="absolute bottom-[-10%] right-[-5%] w-[60vw] h-[60vw] rounded-full bg-indigo-400/30 blur-[140px]" />
        <div className="absolute top-[20%] right-[10%] w-[40vw] h-[40vw] rounded-full bg-cyan-400/30 blur-[120px] animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute bottom-[20%] left-[5%] w-[45vw] h-[45vw] rounded-full bg-teal-400/20 blur-[130px]" />
        
        {/* Clean Glass Overlay */}
        <div className="absolute inset-0 bg-white/70 backdrop-blur-[100px] backdrop-saturate-150" />
      </div>

      {/* Navigation Bar */}
      <nav className="relative z-10 w-full px-12 py-8 flex items-center justify-between">
        <div className="flex items-center gap-2 group cursor-default">
          <div className="relative w-6 h-6 group-hover:scale-110 transition-transform duration-300">
            <img 
              src="/academy_logo.png" 
              alt="HitClicks Logo" 
              className="object-contain w-full h-full grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" 
            />
          </div>
          <span className="text-xs font-black uppercase tracking-[0.15em] text-slate-800 transition-colors group-hover:text-black">{APP_NAME}</span>
        </div>
        <Link 
          to="/login" 
          className="px-8 py-3 rounded-full bg-white/40 backdrop-blur-md border border-slate-200 text-sm font-black uppercase tracking-widest hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 shadow-xl hover:shadow-primary/20 hover:-translate-y-0.5 active:scale-95"
        >
          Sign In
        </Link>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 w-full px-8 flex-1 flex flex-col justify-center items-center text-center">
        {/* Logo above main title */}
        <div className="relative w-36 h-36 mb-6 animate-in fade-in zoom-in duration-1000 delay-75 drop-shadow-2xl hover:scale-105 transition-transform duration-500">
          <img 
            src="/academy_logo_glow.png" 
            alt="HitClicks Academy Logo" 
            className="object-contain w-full h-full"
          />
        </div>

        <h1 className="font-black tracking-tighter leading-none mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-100 drop-shadow-2xl flex flex-col items-center">
          <span className="text-sm md:text-lg uppercase tracking-[0.25em] text-black font-extrabold mb-2">{firstWord}</span>
          <span className="text-5xl md:text-7xl brand-gradient block">{restOfName}.</span>
        </h1>

        {/* Chip below title */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-black uppercase tracking-[0.2em] mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 backdrop-blur-md shadow-lg shadow-primary/5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          Simple steps to grow your business
        </div>

        <p className="max-w-2xl text-sm md:text-base text-slate-600 font-medium leading-relaxed mb-12 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
          A simple and easy-to-use platform to learn, track your progress, and grow your business with the help of our expert coaches.
        </p>

        <div className="flex items-center justify-center animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
          <Link 
            to="/login"
            className="group relative px-10 py-5 brand-bg-gradient text-white rounded-[2rem] font-black text-sm uppercase tracking-widest shadow-[0_0_40px_-10px_rgba(243,82,195,0.5)] hover:shadow-[0_0_60px_-10px_rgba(172,88,197,0.7)] hover:scale-105 active:scale-95 transition-all flex items-center gap-3 overflow-hidden"
          >
            <span className="relative z-10">Start Learning</span>
            <span className="relative z-10 material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </Link>
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="relative z-10 w-full py-8 text-center text-[10px] font-bold text-slate-500 uppercase tracking-widest opacity-60">
        <p>© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
      </footer>
    </div>
  );
}
